<template>
  <FloatingPillButton
    :disabled="editorStore.isGeneratingBackground"
    @click="currentWidgetConfig?.action"
    class="absolute top-4 z-20 generate-widget"
    :variant="currentWidgetConfig.variant"
  >
    <template v-if="!isEmptyState" #start>
      <component :is="iconComponent" />
    </template>

    <template v-if="!editorStore.isGeneratingBackground && !isEmptyState" #center>
      {{ currentWidgetConfig.copy }}
    </template>

    <template v-if="!editorStore.isGeneratingBackground && !isEmptyState && currentWidgetConfig.badgeAvailable" #end>
      <Badge :tone="badgeTone" is-small>
        <div class="text-white flex items-center">
          <CreditLightningIcon />
          <span class="px-1 font-bold">{{ badgeText }}</span>
        </div>
      </Badge>
    </template>
  </FloatingPillButton>
</template>

<script setup lang="ts">
import {
  FloatingPillButton,
  Badge,
  SparkleIcon,
  CreditLightningIcon,
  CircularLoader,
  AiBackgroundIconAll,
} from "prism";
import { nextTick, ref, computed, watch, inject } from "vue";
import { useMagicBackground, WidgetState } from "@/composables/magic_background";
import { MagicBackgroundTemplate } from "@/modules/internal_api/magic_backgrounds";
import { PersistentStore } from "@/stores/persistent_store";
import { useEditorStore } from "@/stores/editor_store";

interface Props {
  template: MagicBackgroundTemplate;
  persistentStore: PersistentStore;
}

const props = defineProps<Props>();
const I18n = inject("I18n");
const editorStore = useEditorStore();

const isEmptyState = ref(false);

const persistentStore = computed(() => props.persistentStore);
const { currentWidgetConfig, currentState, resetError } = useMagicBackground(persistentStore);

const iconComponent = computed(() => {
  switch (currentWidgetConfig.value?.icon) {
    case "generate":
      return AiBackgroundIconAll;
    case "generating":
      return CircularLoader;
    case "generated":
      return AiBackgroundIconAll;
    default:
      return SparkleIcon;
  }
});

const badgeTone = computed(() => (currentState.value === WidgetState.Error ? "red" : "blue"));
const badgeText = computed(() => (currentState.value === WidgetState.Error ? I18n.t("editor.credit_refunded") : "1"));

watch(currentState, (newVal) => {
  if (newVal === WidgetState.Error) {
    setTimeout(() => {
      resetError();
      isEmptyState.value = true;
      nextTick(() => {
        isEmptyState.value = false;
      });
    }, 2000);
  }
});
</script>

<style scoped>
.generate-widget {
  left: 50%;
  transform: translateX(-50%);
}
</style>
